import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { useAuthContext } from '@contexts/AuthContext';
import { Card } from '@components/common';
import { useResource } from '@hooks/useResource';
import { useResourcePermissions } from '@hooks/useResourcePermissions';
import { useModels } from '@hooks/useModels';
import { useNotification } from '@hooks/useNotification';
import { BaseModal } from '@components/modals';
import { ImageManager } from '@components/attachments';
import {
    ResourceInfo,
    ResourceTags,
    ResourceTransactions
} from './components';

import api from '@services/api';
import './ResourceViewPage.scss';


export const ResourceViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { user } = useAuthContext();
    const notify = useNotification();
    const { loading, error, fetchResource, deleteResource } = useResource();
    const { models: allTags, loading: tagsLoading, refetch } = useModels('tags');

    // Permissions pour cette ressource
    const { permissions: resourcePermissions, loading: permissionsLoading } = useResourcePermissions(id);

    // State management
    const [resource, setResource] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, mode: 'delete' });
    const [activeTags, setActiveTags] = useState(new Set());

    // Load resource data and initialize tags
    useEffect(() => {
        const loadResourceData = async () => {
            if (!id) return;

            try {
                const resourceData = await fetchResource(id);

                console.log('=== DEBUG: Resource loaded ===');
                console.log('Resource:', resourceData);
                console.log('Personal?', resourceData?.personal);
                console.log('Owner:', resourceData?.owner);
                console.log('Metadata:', resourceData?.resource_metadata);

                // If resource is personal and owner is not populated, extract from metadata
                if (resourceData?.personal && !resourceData.owner && resourceData.resource_metadata) {
                    console.log('Loading owner from metadata...');
                    const ownedByMeta = resourceData.resource_metadata.find(m => m.metadata_key === 'owned_by');
                    console.log('owned_by metadata:', ownedByMeta);

                    if (ownedByMeta) {
                        try {
                            console.log('Fetching user:', ownedByMeta.value);
                            const ownerResponse = await api.get(`/users/${ownedByMeta.value}`);
                            console.log('Owner loaded:', ownerResponse.data);
                            resourceData.owner = ownerResponse.data;
                        } catch (err) {
                            console.error('Failed to load resource owner:', err);
                        }
                    }
                }

                console.log('Final resource with owner:', resourceData.owner);
                console.log('=== END DEBUG ===');

                setResource(resourceData);

                // Initialize active tags with resource's current tags
                if (resourceData.tags && resourceData.tags.length > 0) {
                    setActiveTags(new Set(resourceData.tags.map(tag =>
                        typeof tag === 'string' ? tag : tag.name
                    )));
                }
            } catch (err) {
                console.error('Failed to load resource:', err);
            }
        };

        loadResourceData();
    }, [id]); // Remove fetchResource from dependencies


    // Load all tags
    useEffect(() => {
        refetch();
    }, []); // Remove refetch from dependencies to avoid loops

    // Navigation handlers
    const handleBack = () => {
        navigate('/resources');
    };

    const handleEdit = () => {
        navigate(`/resources/${id}/edit`);
    };

    const handleDeleteClick = () => {
        setModalState({ isOpen: true, mode: 'delete' });
    };

    const handleModalClose = () => {
        setModalState({ isOpen: false, mode: 'delete' });
    };

    const handleModalSuccess = () => {
        if (modalState.mode === 'delete') {
            navigate('/resources');
        }
    };

    const handleBorrow = () => {
        navigate(`/resources/${id}/borrow`);
    };

    const handleConsume = () => {
        navigate(`/resources/${id}/consume`);
    };

    const handleRefill = () => {
        navigate(`/resources/${id}/refill`);
    };

    const handleReserve = () => {
        navigate(`/resources/${id}/reserve`);
    };

    const handleReturn = () => {
        if (resource?.current_borrowing?.id) {
            navigate(`/borrowings/${resource.current_borrowing.id}/return`);
        }
    };

    // Tag handling
    const handleTagToggle = useCallback(async (tagName) => {
        if (!resource) return;

        try {
            // Find the tag object to get its ID
            const tag = allTags.find(t =>
                (typeof t === 'string' ? t : t.name) === tagName
            );

            if (!tag) return;

            const tagId = typeof tag === 'string' ? null : tag.id;
            if (!tagId) return;

            const isCurrentlyActive = activeTags.has(tagName);

            // Optimistically update the UI
            setActiveTags(prev => {
                const newActiveTags = new Set(prev);
                if (isCurrentlyActive) {
                    newActiveTags.delete(tagName);
                } else {
                    newActiveTags.add(tagName);
                }
                return newActiveTags;
            });

            // Call the appropriate API endpoint
            if (isCurrentlyActive) {
                await api.resourceTags.detach(resource.id, tagId);
            } else {
                await api.resourceTags.attach(resource.id, tagId);
            }

        } catch (error) {
            console.error('Failed to toggle tag:', error);

            // Revert the optimistic update on error
            setActiveTags(prev => {
                const newActiveTags = new Set(prev);
                if (activeTags.has(tagName)) {
                    newActiveTags.delete(tagName);
                } else {
                    newActiveTags.add(tagName);
                }
                return newActiveTags;
            });
        }
    }, [resource]);

    // Calendar event handlers
    const handleSelectEvent = useCallback((event) => {
        // Navigate to borrowing details page
        if (event.originalId) {
            navigate(`/borrowings/${event.originalId}`);
        }
    }, []);

    const handleSelectSlot = useCallback((slotInfo) => {
        console.log('Selected slot:', slotInfo);
    }, []);

    // Render helper functions
    const renderLoadingState = () => (
        <div className="resource-view-page">
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('common.loading')}</p>
            </div>
        </div>
    );

    const renderErrorState = () => (
        <div className="resource-view-page">
            <div className="error-container">
                <h2>{t('resourceView.errorTitle')}</h2>
                <p>{error || t('resourceView.resourceNotFound')}</p>
                <button onClick={handleBack} className="btn btn-primary">
                    {t('common.backToResources')}
                </button>
            </div>
        </div>
    );

    // Main render logic
    if (loading) {
        return renderLoadingState();
    }

    if (error || !resource) {
        return renderErrorState();
    }

    return (
        <div className="resource-view-page">
            <div className="resource-content">
                <div className="details-section">
                    <Card className="resource-info-card">
                        <div className="resource-main-info">
                            <ImageManager
                                entity={resource}
                                editable={false}
                                showUploader={resourcePermissions.canUpdate}
                                maxImages={10}
                                className="gallery-view"
                                maxFileSize={10 * 1024 * 1024} // 10MB for resource images
                                thumbnailSize={300}
                                displayMode="carousel"
                            />
                            <ResourceInfo
                                resource={resource}
                                onBorrow={handleBorrow}
                                onConsume={handleConsume}
                                onRefill={handleRefill}
                                onReserve={handleReserve}
                                onReturn={handleReturn}
                                onEdit={handleEdit}
                                onDelete={handleDeleteClick}
                                resourcePermissions={resourcePermissions}
                                user={user}
                            />
                        </div>

                        {resource.description && (
                            <div className="description-section">
                                <h3>{t('resourceView.description')}</h3>
                                <p className="description">{resource.description}</p>
                            </div>
                        )}

                        {resource.notes && (
                            <div className="notes-section">
                                <h3>{t('resourceView.notes')}</h3>
                                <p className="notes">{resource.notes}</p>
                            </div>
                        )}
                    </Card>
                </div>

                <Card className="resource-transactions-card">
                    <ResourceTransactions
                        resource={resource}
                        onSelectEvent={handleSelectEvent}
                        onSelectSlot={handleSelectSlot}
                    />
                </Card>
            </div>

            <BaseModal
                isOpen={modalState.isOpen}
                onClose={handleModalClose}
                onSuccess={handleModalSuccess}
                mode={modalState.mode}
                item={resource}
                itemType="resource"
                deleteFn={async (id) => await api.delete(`/resources/${id}`)}
                size="small"
            />
        </div>
    );
};

export default ResourceViewPage;